import { useState, useContext, useEffect, useReducer } from 'react';
import { useLazyQuery } from 'data-fetching-client';
import FetchObjectFooter from 'automation-ui-formula-editor/fetch-object-panel/components/FetchObjectFooter';
import { useDataSourcesContext } from 'automation-ui-formula-editor/data-sources/DataSourcesContext';
import { ADD_DATA_SOURCE } from 'automation-ui-formula-editor/data-sources/queries/addDataSourceQuery';
import DocButton from 'automation-ui-usage-tracking/components/DocButton';
import { SupportLinkKeys } from 'automation-ui-usage-tracking/SupportLinks';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIAlert from 'UIComponents/alert/UIAlert';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import UINanoProgress from 'UIComponents/progress/UINanoProgress';
import UsageTrackerContext from 'automation-ui-usage-tracking/UsageTrackerContext';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-usage-tracking/trackingUtils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import useAddDataSourceLimit from 'automation-ui-formula-editor/fetch-object-panel/hooks/useAddDataSourceLimit';
import { getDataSourceRelationForEventTracking } from 'automation-ui-formula-editor/fetch-object-panel/utils/getDataSourceRelationForEventTracking';
import addDataSourceReducer, { AddDataSourceInitState } from 'automation-ui-formula-editor/fetch-object-panel/reducer/addDataSourceReducer';
import AddDataSourceFilterRecordsByTypes from 'automation-ui-formula-editor/fetch-object-panel/constants/AddDataSourceFilterRecordsByTypes';
import isSaveEnabledValidation from 'automation-ui-formula-editor/fetch-object-panel/utils/isSaveEnabledValidation';
import { getFilterRecordsByType } from 'automation-ui-formula-editor/fetch-object-panel/utils/getFilterRecordsByType';
import { Text } from 'foundations-components/components/Text';
import DataSourceForm from 'automation-ui-formula-editor/fetch-object-panel/containers/DataSourceForm';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AddDataSource = ({
  onClose,
  onCloseAndScrollToDataSource,
  flowObjectTypeId,
  objectTypeFilter
}) => {
  const {
    usageTracker
  } = useContext(UsageTrackerContext);
  const {
    flowId
  } = useDataSourcesContext();
  const [componentState, dispatch] = useReducer(addDataSourceReducer, AddDataSourceInitState);
  const [addDataSource, {
    called: addDataSourceCalled,
    loading: addDataSourceLoading,
    error: addDataSourceError,
    data: newDataSourceData
  }] = useLazyQuery(ADD_DATA_SOURCE);
  const {
    dataSourceCount,
    isOverDataSourceCountLimit
  } = useAddDataSourceLimit();
  const [associationSelectedName, setAssociationSelectedName] = useState('');
  const filterRecordsByType = getFilterRecordsByType(componentState);
  useEffect(() => {
    trackEvent(usageTracker, Trackables.VIEWED_ADD_DATA_SOURCE, {
      screen: 'Add Data Source Panel'
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!newDataSourceData) {
      return;
    }
    const {
      createdDataSource
    } = newDataSourceData.addDataSource;
    onCloseAndScrollToDataSource({
      addedDataSourceName: createdDataSource
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newDataSourceData]);
  const {
    isSaveEnabled,
    saveValidationMessage
  } = isSaveEnabledValidation({
    componentState,
    isOverDataSourceCountLimit
  });
  const onSave = () => {
    if (!isSaveEnabled) {
      return;
    }
    const type = filterRecordsByType === AddDataSourceFilterRecordsByTypes.ASSOCIATION_LABEL ? 'associated' : filterRecordsByType;
    trackEvent(usageTracker, Trackables.SAVED_A_DATA_SOURCE_TO_WORKFLOW, {
      screen: 'Add Data Source Panel',
      flowObjectTypeId,
      objectTypeIdSelected: componentState.objectTypeId,
      category: getDataSourceRelationForEventTracking(associationSelectedName),
      type,
      sortField: componentState.recordFilterDefinition.selectRecordBy
    });
    addDataSource({
      variables: {
        flowId,
        data: componentState
      }
    });
  };
  return dataSourceCount < 0 ? /*#__PURE__*/_jsx(UIDialogBody, {
    children: /*#__PURE__*/_jsx(UINanoProgress, {
      value: 75
    })
  }) : /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(UIDialogBody, {
      children: [isOverDataSourceCountLimit && /*#__PURE__*/_jsxs(UIAlert, {
        className: "m-bottom-5",
        type: "danger",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h5",
          paddingBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-formula-editor.fetch-object-panel.add-data-source.over-data-source-limit.title"
          })
        }), /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "automation-ui-formula-editor.fetch-object-panel.add-data-source.over-data-source-limit.text"
        })]
      }), !!addDataSourceError && /*#__PURE__*/_jsxs(UIAlert, {
        className: "m-bottom-5",
        type: "danger",
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h5",
          paddingBlockEnd: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-formula-editor.fetch-object-panel.add-data-source.saving.error-saving.title"
          })
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.fetch-object-panel.add-data-source.saving.error-saving.description"
        })]
      }), /*#__PURE__*/_jsx(Text, {
        as: "h5",
        marginBlockEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.fetch-object-panel.add-data-source.header.title"
        })
      }), /*#__PURE__*/_jsx(Text, {
        marginBlockEnd: "100",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "automation-ui-formula-editor.fetch-object-panel.add-data-source.header.text_jsx",
          elements: {
            SupportLink: ({
              children
            }) => /*#__PURE__*/_jsx(DocButton, {
              supportKey: SupportLinkKeys.LEARN_MORE_ABOUT_ADD_DATA_SOURCE,
              children: children
            })
          }
        })
      }), /*#__PURE__*/_jsx(Text, {
        typography: "detail-200",
        marginBlockEnd: "500",
        children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "automation-ui-formula-editor.fetch-object-panel.add-data-source.header.sub-text"
        })
      }), /*#__PURE__*/_jsx(DataSourceForm, {
        componentState: componentState,
        dispatch: dispatch,
        flowObjectTypeId: flowObjectTypeId,
        objectTypeFilter: objectTypeFilter,
        setAssociationSelectedName: setAssociationSelectedName,
        isNewDataSource: true
      }), addDataSourceCalled && addDataSourceLoading && /*#__PURE__*/_jsx(UILoadingOverlay, {
        contextual: true,
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "automation-ui-formula-editor.fetch-object-panel.add-data-source.saving.is-saving-text"
        }),
        showLabel: true,
        layout: "centered"
      })]
    }), !addDataSourceLoading && /*#__PURE__*/_jsx(FetchObjectFooter, {
      onClose: onClose,
      onSave: onSave,
      isSaving: addDataSourceLoading,
      onSaveDisabled: !isSaveEnabled,
      onSaveDisabledTooltip: saveValidationMessage
    })]
  });
};
export default AddDataSource;